import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getIngredients, loadRecipes, Recipe, recipeUrl } from "../data";
import {
    Box,
    Collapse,
    IconButton,
    IconButtonProps,
    Input,
    InputAdornment,
    MenuItem,
    MenuList,
    Stack,
    styled,
    Tab,
    Tabs,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AisleHeader from "./base/AisleHeader";
import { noneCategoryId } from "../contexts/ShoppingListContext";
import { useCurrentHouseholdId } from "../contexts/AuthProvider";
import RecipeMenuItem from "./base/RecipeMenuItem";
import BaseIngredients from "./BaseIngredients";
import { IngredientOption } from "./AddIngredient";

interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme }) => ({
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
        duration: theme.transitions.duration.shortest,
    }),
    variants: [
        {
            props: ({ expand }) => !expand,
            style: {
                transform: "rotate(0deg)",
            },
        },
        {
            props: ({ expand }) => !!expand,
            style: {
                transform: "rotate(180deg)",
            },
        },
    ],
}));

interface AisleWithIngredients {
    uniqueId: string;
    id?: number;
    name?: string;
    sort_order: number;
    ingredients: string[];
}

interface IngredientReference {
    recipes: Recipe[];
}

interface AisleProps {
    aisle: AisleWithIngredients;
    recipes: Map<string, IngredientReference> | undefined;
    forceOpen?: boolean;
    selectedIngredient?: string;
    onSelectIngredient?: (ingredient: string) => void;
}

function Aisle({ aisle, recipes, forceOpen, selectedIngredient, onSelectIngredient }: AisleProps) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <h2>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AisleHeader
                        uniqueId={aisle.uniqueId}
                        id={aisle.id}
                        sort_order={aisle.sort_order}
                        name={aisle.name}
                    ></AisleHeader>
                    <ExpandMore
                        expand={open}
                        onClick={() => setOpen(!open)}
                        aria-expanded={open}
                        aria-label="show more"
                    >
                        <ExpandMoreIcon />
                    </ExpandMore>
                </Stack>
            </h2>
            <Collapse in={forceOpen || open} timeout="auto" unmountOnExit>
                <MenuList dense>
                    {aisle.ingredients.map(ingredient => {
                        const isSelected = selectedIngredient === ingredient;
                        const recipesWithIngredients = recipes?.get(ingredient)?.recipes || [];
                        return (
                            <MenuItem
                                key={ingredient}
                                selected={isSelected}
                                onClick={() => onSelectIngredient?.(ingredient)}
                            >
                                <Stack direction="column">
                                    {ingredient} {recipes ? recipesWithIngredients.length : "?"}
                                    <Collapse in={isSelected} timeout="auto" unmountOnExit mountOnEnter>
                                        {recipesWithIngredients.map(recipe => (
                                            <RecipeMenuItem
                                                key={"r" + recipe.id}
                                                url={recipeUrl(recipe)}
                                                name={recipe.name}
                                                imageId={recipe.image_id}
                                            ></RecipeMenuItem>
                                        ))}
                                    </Collapse>
                                </Stack>
                            </MenuItem>
                        );
                    })}
                </MenuList>
            </Collapse>
        </>
    );
}

export function AllIngredients({ ingredients }: { ingredients?: AisleWithIngredients[] }) {
    const currentHouseholdId = useCurrentHouseholdId();
    const [recipes, setRecipes] = useState<Map<string, IngredientReference> | undefined>();
    const [query, setQuery] = useState<string>("");
    const [selectedIngredient, setSelectedIngredient] = useState<string | undefined>();

    useEffect(() => {
        loadRecipes(currentHouseholdId).then(recipes => {
            let map = new Map<string, IngredientReference>();
            for (const recipe of recipes) {
                for (const ingredient of recipe.ingredients) {
                    const name = ingredient.toLowerCase();
                    let entry = map.get(name);
                    if (!entry) {
                        entry = { recipes: [] };
                        map.set(name, entry);
                    }
                    entry.recipes.push(recipe);
                }
            }
            setRecipes(map);
        });
    }, [currentHouseholdId]);

    const onSelectIngredient = useCallback(
        (ingredient: string) => {
            setSelectedIngredient(selectedIngredient => {
                return selectedIngredient === ingredient ? undefined : ingredient;
            });
        },
        [setSelectedIngredient]
    );

    if (!ingredients) {
        return <div>Loading...</div>;
    }

    let ingredientsFiltered = ingredients;
    if (query) {
        const lowerQuery = query.toLowerCase();
        ingredientsFiltered = ingredients
            .map(aisle => ({
                ...aisle,
                ingredients: aisle.ingredients.filter(ingredient => ingredient.toLowerCase().includes(lowerQuery)),
            }))
            .filter(aisle => aisle.ingredients.length > 0);
    }

    return (
        <Stack direction="column" sx={{ padding: "15px" }}>
            <Input
                type="text"
                value={query}
                onChange={event => setQuery(event.target.value)}
                endAdornment={
                    <InputAdornment position="end" sx={{ visibility: query ? "visible" : "hidden" }}>
                        <IconButton aria-label="clear search" onClick={() => setQuery("")} edge="end">
                            <ClearIcon />
                        </IconButton>
                    </InputAdornment>
                }
                placeholder="Sök"
            />
            {ingredientsFiltered.map(aisle => (
                <Aisle
                    key={aisle.uniqueId}
                    aisle={aisle}
                    recipes={recipes}
                    forceOpen={!!query}
                    selectedIngredient={selectedIngredient}
                    onSelectIngredient={onSelectIngredient}
                ></Aisle>
            ))}
        </Stack>
    );
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            style={{ flexGrow: 1, display: value !== index ? "none" : "flex" }}
            {...other}
        >
            {value === index && <Box sx={{ p: 3, flexGrow: 1, display: "flex" }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

export default function IngredientsView() {
    const [activeTab, setActiveTab] = useState(0);
    const [ingredients, setIngredients] = useState<AisleWithIngredients[] | undefined>();

    const ingredientOptions: IngredientOption[] = useMemo(() => {
        const ingredientOptions: IngredientOption[] = [];
        if (!ingredients) return ingredientOptions;

        for (const aisle of ingredients) {
            for (const ingredient of aisle.ingredients) {
                ingredientOptions.push({ name: ingredient, category: aisle.name });
            }
        }
        return ingredientOptions;
    }, [ingredients]);

    useEffect(() => {
        getIngredients().then(res => {
            let aisles: AisleWithIngredients[] = res.aisles.map(aisle => ({
                uniqueId: "" + aisle.id,
                id: aisle.id,
                name: aisle.name,
                sort_order: aisle.sort_order,
                ingredients: aisle.ingredients,
            }));

            if (res.unsorted.length > 0) {
                aisles.push({
                    uniqueId: noneCategoryId,
                    sort_order: Number.MAX_SAFE_INTEGER,
                    ingredients: res.unsorted,
                });
            }

            setIngredients(aisles);
        });
    }, [setIngredients]);

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    return (
        <Stack sx={{ width: "100%" }} direction="column" flexGrow={1}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={activeTab} onChange={handleChange} aria-label="basic tabs example">
                    <Tab label="Alla" {...a11yProps(0)} />
                    <Tab label="Basvaror" {...a11yProps(1)} />
                </Tabs>
            </Box>
            <TabPanel value={activeTab} index={0}>
                <AllIngredients ingredients={ingredients}></AllIngredients>
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
                <BaseIngredients ingredients={ingredientOptions}></BaseIngredients>
            </TabPanel>
        </Stack>
    );
}
